import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Sun, Building2, Target, FileText, LineChart, Plug, Database, Settings, LogOut,
  ShieldCheck, Crown, Radar,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { ROLE_LABEL } from '@/lib/format';
import recoupLogo from '@/assets/recoup-logo.png';

// The simple PM workspace: only three destinations.
const PM_GROUPS = [
  { label: 'Workspace', items: [
    { label: "Today's Work", route: '/', icon: Sun, testid: 'nav-today', end: true },
    { label: 'Projects', route: '/projects', icon: Building2, testid: 'nav-projects' },
    { label: 'Opportunities', route: '/opportunities', icon: Target, testid: 'nav-opportunities' },
  ]},
];

// Everything enterprise/founder-only lives here and is hidden from PMs.
const FOUNDER_GROUPS = [
  { label: 'Founder Tools', items: [
    { label: 'Founder Analytics', route: '/founder', icon: Crown, testid: 'nav-founder' },
    { label: 'Validation', route: '/validation', icon: ShieldCheck, testid: 'nav-validation' },
    { label: 'Analytics', route: '/analytics', icon: LineChart, testid: 'nav-analytics' },
    { label: 'Detections (raw)', route: '/detections', icon: Radar, testid: 'nav-detections' },
    { label: 'Change Orders', route: '/change-orders', icon: FileText, testid: 'nav-change-orders' },
  ]},
  { label: 'Configure', items: [
    { label: 'Cost Database', route: '/cost-database', icon: Database, testid: 'nav-cost-database' },
    { label: 'Integrations', route: '/integrations', icon: Plug, testid: 'nav-integrations' },
    { label: 'Settings', route: '/settings', icon: Settings, testid: 'nav-settings' },
  ]},
];

export const SidebarNav = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const initials = (user?.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('');
  const groups = user?.is_founder ? [...PM_GROUPS, ...FOUNDER_GROUPS] : PM_GROUPS;
  return (
    <div className="flex h-full flex-col bg-ink-900">
      <div className="flex items-center px-5 h-16 border-b border-white/5">
        <img src={recoupLogo} alt="Recoup" className="h-9 w-auto select-none" data-testid="sidebar-logo" draggable="false" />
      </div>

      <nav className="flex-1 overflow-y-auto shell-scroll px-3 py-4 space-y-5">
        {groups.map((g) => (
          <div key={g.label}>
            <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{g.label}</div>
            <div className="space-y-0.5">
              {g.items.map((it) => (
                <NavLink
                  key={it.route} to={it.route} end={it.end} onClick={onNavigate} data-testid={it.testid}
                  className={({ isActive }) => cn(
                    'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors duration-150',
                    isActive ? 'bg-white/[0.07] text-white' : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-100'
                  )}
                >
                  {({ isActive }) => (
                    <>
                      <span className={cn('absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-money transition-opacity', isActive ? 'opacity-100' : 'opacity-0')} />
                      <it.icon className={cn('h-[18px] w-[18px] shrink-0', isActive ? 'text-money' : 'text-slate-500 group-hover:text-slate-300')} />
                      <span>{it.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-white/5">
        <div className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-white/[0.04] transition-colors">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 text-white text-xs font-bold flex items-center justify-center shrink-0">{initials}</div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{user?.is_founder ? 'Founder' : (ROLE_LABEL[user?.role] || user?.role)}</div>
          </div>
          <button onClick={logout} data-testid="sidebar-logout" title="Sign out"
            className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
